import React, { useState, useEffect } from "react";
import { X, Palette, ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const TINT = {
  sapphire: { border: "rgba(15, 82, 186, 0.35)", glow: "rgba(15, 82, 186, 0.18)" },
  ruby:     { border: "rgba(224, 17, 95, 0.35)", glow: "rgba(224, 17, 95, 0.16)" },
  emerald:  { border: "rgba(80, 200, 120, 0.35)", glow: "rgba(80, 200, 120, 0.16)" },
};

// Availability of color chart assets per gem
const AVAILABLE = {
  emerald: true,
  sapphire: true,
  ruby: true,
};

const srcFor = (gem, lang) => `/fiches/color-charts/${gem}-${lang === "fr" ? "fr" : "en"}.png`;

export const ColorChartModal = ({ open, onClose, gemType = "emerald" }) => {
  const { lang, t } = useLang();
  const [zoom, setZoom] = useState(false);

  useEffect(() => { if (!open) setZoom(false); }, [open]);
  if (!open) return null;

  const tint = TINT[gemType] || TINT.emerald;
  const src = srcFor(gemType, lang);
  const available = AVAILABLE[gemType];

  const gemLabel = t.gems[gemType];
  const heading = lang === "fr"
    ? `Nuancier — ${gemLabel}`
    : `Color Chart — ${gemLabel}`;
  const subhead = lang === "fr"
    ? "Guide visuel indicatif de teinte, ton et saturation"
    : "Indicative visual guide to hue, tone and saturation";

  return (
    <div
      data-testid="color-chart-modal"
      className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 bg-black/85 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl my-6 rounded-sm bg-[#050B1E] border p-4 md:p-10"
        style={{ borderColor: tint.border, boxShadow: `inset 0 0 120px ${tint.glow}` }}
      >
        <button
          data-testid="color-chart-close-btn"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#A1A7B3] hover:text-[#F8F5EE] transition-colors z-10"
          aria-label={t.modal.close}
        >
          <X size={22} strokeWidth={1.4} />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <Palette size={22} strokeWidth={1.4} className="text-[#C1A661]" />
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C1A661]">
              {lang === "fr" ? "Nuancier de référence" : "Reference color chart"}
            </div>
            <h2 className="font-serif text-2xl md:text-4xl font-light text-[#F8F5EE] leading-tight">
              {heading}
            </h2>
            <p className="mt-1 text-[12px] text-[#A1A7B3] font-light italic">{subhead}</p>
          </div>
        </div>

        {available ? (
          <div className="space-y-2" data-testid="color-chart-content">
            <button
              type="button"
              onClick={() => setZoom(true)}
              className="block w-full text-left"
              aria-label={t.fiche.viewFullSize}
            >
              <img
                src={src}
                alt={heading}
                data-testid="color-chart-image"
                className="w-full h-auto rounded-sm border cursor-zoom-in hover:brightness-105 transition"
                style={{ borderColor: tint.border, backgroundColor: "#F8F5EE" }}
                loading="lazy"
              />
            </button>
            <button
              type="button"
              onClick={() => setZoom(true)}
              className="inline-block text-[10px] tracking-[0.25em] uppercase text-[#C1A661] hover:text-[#E2CFA4] transition-colors"
            >
              {t.fiche.viewFullSize} ↗
            </button>
          </div>
        ) : (
          <div className="rounded-sm border border-[#C1A661]/20 bg-[#080C17] p-8 text-center">
            <p className="text-[13px] text-[#A1A7B3] font-light italic">
              {lang === "fr" ? "Nuancier bientôt disponible." : "Color chart coming soon."}
            </p>
          </div>
        )}
      </div>

      {zoom && available && (
        <div
          data-testid="color-chart-zoom-overlay"
          className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
          onClick={(e) => { e.stopPropagation(); setZoom(false); }}
        >
          <div className="flex items-center justify-between p-4 border-b border-[#C1A661]/20 bg-[#050B1E]/95">
            <button
              onClick={(e) => { e.stopPropagation(); setZoom(false); }}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] text-[#E2CFA4] hover:text-[#F8F5EE] text-xs tracking-[0.25em] uppercase transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              <span>{lang === "fr" ? "Retour" : "Back"}</span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setZoom(false); }}
              className="text-[#A1A7B3] hover:text-[#F8F5EE] transition-colors"
              aria-label={t.modal.close}
            >
              <X size={22} strokeWidth={1.4} />
            </button>
          </div>
          <div className="flex-1 overflow-auto p-4 md:p-8 flex items-start justify-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={src}
              alt={heading}
              className="max-w-full h-auto rounded-sm shadow-2xl"
              style={{ backgroundColor: "#F8F5EE" }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
